/**
 * Maps every `MIGRATION_*` validation issue code to its WagePro i18n key.
 *
 * The server returns issues as `{ code, params }` and NEVER a prose message
 * (it must never echo a NIC / bank number — see `migration_template.ts`). The
 * browser translates each `code` to a plain-language, how-to-fix message
 * client-side via these keys, interpolating `issue.params` with ICU
 * single-brace placeholders.
 *
 * `migration_issue_messages.test.ts` diffs this map against
 * `MIGRATION_ISSUE_CODES` from `@jumpstone/core` in BOTH directions, so a new
 * server code can never ship without a key here (and a stale key here can
 * never outlive its code).
 */
import {
  MIGRATION_ISSUE_CODES,
  type MigrationIssueCode,
} from '@jumpstone/core';

/**
 * `code → 'migration.issue.<CODE>'` i18n key — WITHOUT the `wp.` namespace
 * prefix. `useTranslation('wp')` prepends `wp.` itself, so the key passed to
 * `t()` must be namespace-relative (the full catalog key is
 * `wp.migration.issue.<CODE>`).
 */
export const MIGRATION_ISSUE_MESSAGE_KEYS: Record<MigrationIssueCode, string> =
  Object.fromEntries(
    MIGRATION_ISSUE_CODES.map((code) => [code, `migration.issue.${code}`]),
  ) as Record<MigrationIssueCode, string>;

/** The i18n key for a single issue code. */
export function migrationIssueMessageKey(code: MigrationIssueCode): string {
  return MIGRATION_ISSUE_MESSAGE_KEYS[code];
}
